import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  JsonPreGerado,
  LinhaResumoGuia,
  LinhaResumoItemGuia,
  OperadoraCompetenciaParams,
} from '@gestor-web/@core/types';
import { HEADER_SKIP_LOAD_INTERCEPTOR } from '@gestor-web/app/shared/services';
import { environment } from '@gestor-web/environments/environment';
import { Api } from '../api';
import {
  ApiResponse,
  ArquivoGerado,
  CnsRepetidos,
  CnsRepetidosGuias,
  ContasRepetidas,
  CpfRepetidos,
  CpfRepetidosGuias,
  DispersaoUrgenciaEmergencia,
  FornecimentoDiretoDetalhe,
  JSON_PRE_GERADO_TIPO,
  JsonPreGeradoLinhas,
  JsonPreGeradoResponse,
  OperadoraCidadesAbrangidas,
  OperadoraCidadesAbrangidasFiltroItem,
  OperadoraModulo,
  OperadoraPlanoSaude,
  ProcedimentosElegiveis,
  ValidacaoLotesXte,
} from './model';
import { MediaBeneficiarios, ResumoDasMetricasApiItem } from './model/resumo-cnx';

/**
 *  ORDS-API para Simulador
 */
@Injectable({ providedIn: 'root' })
export class SimuladorApi extends Api {
  constructor(http: HttpClient) {
    super(`${environment.API_ORDS_BASE_PATH}/simulador`, http);
  }

  /**
   * Busca dos dados com JSON pré-gerado.
   */
  fetchDadosJsonPreGerado<T extends JsonPreGeradoLinhas>(
    regans: string | number,
    consulta: JSON_PRE_GERADO_TIPO,
    filtro: { anobase?: number; anobase_competencia?: number; competencia?: string; valorcampochave?: number },
  ) {
    const path = `/consuta-json-pre-gerado/operadora/${regans}/consulta/${consulta}`;
    return this.get<ApiResponse<JsonPreGeradoResponse<T>>>(path, filtro);
  }

  /**
   * Busca os dados do Resumo de Guias.
   */
  // fetchDadosResumoGuiasCubo({ regans, ...params }: OperadoraCompetenciaParams) {
  //   return this.get<Response<ResumoGuia>(`/guias/resumo-guias/operadora/${regans}`, { ...params });
  // }

  /**
   * Busca os dados do Resumo de Guias com JSON pré-gerado.
   */
  fetchDadosResumoGuiasCuboJsonPreGerado({
    regans,
    ...params
  }: OperadoraCompetenciaParams & { cd_unidade_list?: string }) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<JsonPreGerado<LinhaResumoGuia>>>(
      `/consuta-json-pre-gerado/operadora/${regans}/consulta/MON_CR_GUIA_RESUMO_V202403`,
      { ...trimmed },
    );
  }

  /**
   * Busca os dados do Resumo de Itens de Guias.
   */
  // fetchDadosResumoItensGuiasCubo({ regans, ...params }: OperadoraCompetenciaParams) {
  //   return this.get<Response<ResumoItemGuia>>(`/guias/resumo-itens-guias/operadora/${regans}`, {
  //     ...params,
  //   });
  // }

  /**
   * Busca os dados do Resumo de Itens de Guias com JSON pré-gerado.
   */
  fetchDadosResumoItensGuiasCuboJsonPreGerado({ regans, ...params }: OperadoraCompetenciaParams) {
    return this.get<ApiResponse<JsonPreGerado<LinhaResumoItemGuia>>>(
      `/consuta-json-pre-gerado/operadora/${regans}/consulta/MON_CR_GUIA_ITEM_RESUMO_V202403`,
      { ...params },
    );
  }

  /**
   * Realiza download do arquivo informado.
   *
   * Reporta o progresso para notificar para o usuário.
   *
   * Desativa load interceptor para não ser cancelado enquanto o usuário navega em outras telas.
   */
  downloadArquivoGerado(regans: string, arquivo: ArquivoGerado) {
    return this.http.get(`${this.basePath}/arquivos/operadora/${regans}/downloads/${arquivo.cd_arquivo}`, {
      observe: 'events',
      responseType: 'blob',
      reportProgress: true,
      headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
    });
  }

  downloadLoteXte(regans: string, arquivo: ValidacaoLotesXte) {
    return this.http.get(
      `${this.basePath}/arquivos/operadora/${regans}/lotes-xte/downloads-log/${arquivo.cd_arquivo}`,
      {
        responseType: 'blob',
      },
    );
  }

  fetchModulosDasOperadoras(regans: string) {
    return this.get<ApiResponse<OperadoraModulo>>(`/operadoras/${regans}/modulos`);
  }

  fetchCnsRepetido({ regans, ...params }: OperadoraCompetenciaParams) {
    return this.get<ApiResponse<CnsRepetidos>>(`/analises/cns-repetido/operadora/${regans}`, {
      ...params,
    });
  }

  fetchCnsRepetidoListaGuias({ regans, ...params }: OperadoraCompetenciaParams & { cd_guia_monitor: string }) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<CnsRepetidosGuias>>(`/analises/cns-repetido/operadora/${regans}/listagem-guias`, {
      ...trimmed,
    });
  }

  fetchCpfRepetido({ regans, ...params }: OperadoraCompetenciaParams) {
    return this.get<ApiResponse<CpfRepetidos>>(`/analises/cpf-repetido/operadora/${regans}`, {
      ...params,
    });
  }

  fetchCpfRepetidoListaGuias({ regans, ...params }: OperadoraCompetenciaParams & { cd_guia_monitor: string }) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<CpfRepetidosGuias>>(`/analises/cpf-repetido/operadora/${regans}/listagem-guias`, {
      ...trimmed,
    });
  }

  fetchProcedimentosElegiveis({ regans, ...params }: OperadoraCompetenciaParams) {
    return this.get<ApiResponse<ProcedimentosElegiveis>>(`/analises/procedimentos-elegiveis/operadora/${regans}`, {
      ...params,
    });
  }

  fetchDispersaoUrgenciaEmergencia({ regans, ...params }) {
    return this.get<ApiResponse<DispersaoUrgenciaEmergencia>>(
      `/analises/dispersao-urgencia-emergencia/operadora/${regans}`,
      {
        ...params,
      },
    );
  }

  fetchFornecimentoDiretoDetalhe(regans: string, cd_guia_monitor: string) {
    return this.get<FornecimentoDiretoDetalhe>(
      `/remuneracao/outras-remuneracoes/operadora/${regans}/fornecimento-direto/lancamentos/${cd_guia_monitor}`,
    );
  }

  fetchOperadoraPlanosSaude(regans: string) {
    return this.get<ApiResponse<OperadoraPlanoSaude>>(`/operadoras/operadora/${regans}/planos-de-saude`);
  }

  fetchOperadoraCidadesAbrangidas(
    regans: string,
    filtros?: {
      nr_registro_plano?: string;
      sgmt_assistencial?: string;
      contratacao?: string;
      id_vigencia_plano?: string;
      situacao_plano?: string;
    },
  ) {
    const params = this.trimParams(filtros);
    return this.get<ApiResponse<OperadoraCidadesAbrangidas>>(`/operadoras/operadora/${regans}/cidades-abrangidas`, {
      ...params,
    });
  }

  fetchOperadoraFiltrosCidadesAbrangidas(regans: string) {
    return this.get<ApiResponse<OperadoraCidadesAbrangidasFiltroItem>>(
      `/operadoras/operadora/${regans}/cidades-abrangidas/filtros`,
    );
  }

  fetchResumoDasMetricas(regans: string) {
    return this.get<ApiResponse<ResumoDasMetricasApiItem>>(`/operadoras/operadora/${regans}/resumo-das-metricas`);
  }

  fetchMediaBeneficiarios({ regans, ...params }: OperadoraCompetenciaParams) {
    return this.get<ApiResponse<MediaBeneficiarios>>(`/operadoras/operadora/${regans}/media-de-beneficiarios`, {
      ...params,
    });
  }

  fetchContasRepetidas({
    regans,
    condicao,
    data_realizacao,
    page,
    size,
    ...params
  }: OperadoraCompetenciaParams & { condicao: string; data_realizacao: string; page?: number; size?: number }) {
    return this.get<ApiResponse<ContasRepetidas>>(`/analises/conformidade/contas-repetidas/operadora/${regans}`, {
      ...params,
      condicao,
      data_realizacao,
      page,
      size,
    });
  }
}
